"use client";

import { X } from "lucide-react";
import Image from "next/image";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import IconWhatsAppGreen from "../Icons/IconWhatsappGreen";
import { useWhatsAppWidget } from "@/contexts/WhatsAppWidgetContext";
import { usePathname } from "@/i18n/routing";
import { trackOpenAIWhatsAppChat } from "@/lib/analytics/openai-events";
import whatsAppChatBackground from "@/public/assets/images/whatsapp-chat-bg.jpg";
import whatsAppAvatar from "@/public/assets/images/whatsapp-icon.jpeg";

const WhatsAppWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("97143772503");
  const [currentTime, setCurrentTime] = useState("");

  const { isWidgetVisible } = useWhatsAppWidget();
  const t = useTranslations("Common.whatsApp");

  const chatBoxRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    setCurrentTime(new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }));
  }, []);

  useEffect(() => {
    if (chatBoxRef.current) {
      const chatBox = chatBoxRef.current;
      if (isOpen) {
        chatBox.style.opacity = "1";
        chatBox.style.transform = "translateY(0)";
      } else {
        chatBox.style.opacity = "0";
        chatBox.style.transform = "translateY(20px)";
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const isBankValuation = pathname.startsWith("/bank-valuation");
    const isCarDetailsPage = pathname.startsWith("/buy-used-cars/vehicle");

    if (isBankValuation) {
      setPhoneNumber("97143772526");
    } else {
      setPhoneNumber("97143772503");
    }

    if (isCarDetailsPage) {
      setMessage(`Hi Alba Cars,\nI'm interested in this car:\n\n${window.location.href}\n\nCould you assist me?`);
    } else if (isBankValuation) {
      setMessage(`Hello Alba Cars.\nIn regards to bank valuation... \n\n`);
    } else {
      setMessage(`Hi Alba Cars,\nI'm interested in a car and need some help.\nCould you assist me?`);
    }
  }, [pathname]);

  const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

  const handleToggleChat = () => setIsOpen(!isOpen);

  const handleChatOnWhatsAppClick = () => {
    const context = pathname.startsWith("/bank-valuation")
      ? "bank_valuation"
      : pathname.startsWith("/buy-used-cars/vehicle")
        ? "car_details"
        : "general";

    trackOpenAIWhatsAppChat({
      source: "whatsapp_widget",
      context,
      page_path: pathname,
      phone_number: phoneNumber,
    });
    handleToggleChat();
  };

  if (!isWidgetVisible) return null;

  return (
    <div className="fixed bottom-4 right-2 z-40 flex flex-col items-end ixs:right-4">
      {/* Chat Box */}
      <div
        ref={chatBoxRef}
        className={`absolute right-0 mb-2 h-[310px] w-[300px] overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 ease-in-out ixs:w-[360px] ${
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={{
          bottom: "65px",
          opacity: "0",
          transform: "translateY(20px)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between bg-[#00826a] px-4 py-5 text-white">
          <div className="relative flex items-center gap-3">
            <span className="absolute bottom-0 flex h-2.5 w-2.5 rounded-full border border-[#00826a] bg-[#25D366] ltr:left-10 rtl:right-[0.2rem]"></span>
            <Image
              src={whatsAppAvatar}
              width={52}
              height={52}
              alt="WhatsApp Contact"
              className="rounded-full"
              placeholder="blur"
            />
            <div className="inter">
              <h3 className="text-base font-semibold">Alba Cars</h3>
              <p className="text-sm text-white opacity-80">{t("online")}</p>
            </div>
          </div>
          <button
            onClick={handleToggleChat}
            className="self-start text-white hover:text-gray-200"
            aria-label={t("closeChat")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Chat Content */}
        <div
          className="relative p-4"
          style={{
            backgroundImage: `url(${whatsAppChatBackground.src})`,
            backgroundSize: "cover",
            backgroundRepeat: "repeat",
            minHeight: "220px",
          }}
        >
          <div className="inter mb-2 text-center text-[0.65rem] text-gray-400">{currentTime}</div>

          {/* Message bubble */}
          <div className="inter relative ms-2 inline-block max-w-[80%] rounded-2xl bg-white p-3 shadow-sm">
            <div
              style={{
                position: "absolute",
                width: 0,
                height: 0,
                borderTop: "15px solid white",
                borderLeft: "15px solid transparent",
                borderRight: "15px solid transparent",
                top: "0",
                left: "-15px",
              }}
            />
            <div
              style={{
                position: "absolute",
                width: 0,
                height: 0,
                borderTop: "17px solid rgba(0,0,0,0.1)",
                borderLeft: "16px solid transparent",
                borderRight: "16px solid transparent",
                top: "-1px",
                left: "-17px",
                zIndex: -1,
              }}
            />
            <p className="mb-2 text-sm">{t("hiThere")}</p>
            <p className="text-sm">{t("howCanIHelp")}</p>
          </div>

          {/* WhatsApp CTA Button */}
          <div className="inter absolute bottom-4 left-0 right-0 flex justify-center">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleChatOnWhatsAppClick}
              className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-green-500"
              aria-label={t("chatOnWhatsApp")}
              tabIndex={0}
            >
              <IconWhatsAppGreen size={26} />
              {t("chatOnWhatsApp")}
            </a>
          </div>
        </div>
      </div>

      {/* Floating Button */}
      <button
        onClick={handleToggleChat}
        className="relative rounded-full bg-[#25D366] p-3 text-white shadow-lg transition-all duration-300 hover:cursor-pointer hover:bg-[#33e474]"
        aria-label={t("openChat")}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleToggleChat();
        }}
      >
        <IconWhatsAppGreen size={40} />
        <span className="absolute right-0 top-2 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-red-500 text-xs text-white"></span>
      </button>
    </div>
  );
};

export default WhatsAppWidget;
